import { forwardRef, InputHTMLAttributes } from 'react';

/**
 * Input component matching Bootstrap .form-control styles EXACTLY
 *
 * Source of truth from bootstrap-3.4.1.min.css:
 *
 * .form-control {
 *   display: block;
 *   width: 100%;
 *   height: 34px;
 *   padding: 6px 12px;
 *   font-size: 14px;
 *   line-height: 1.42857143;
 *   color: #555;
 *   background-color: #fff;
 *   background-image: none;
 *   border: 1px solid #ccc;
 *   border-radius: 4px;
 *   box-shadow: inset 0 1px 1px rgba(0,0,0,.075);
 *   transition: border-color ease-in-out .15s, box-shadow ease-in-out .15s;
 * }
 *
 * .form-control:focus {
 *   border-color: #66afe9;
 *   outline: 0;
 *   box-shadow: inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102,175,233,.6);
 * }
 *
 * .form-control[disabled], .form-control[readonly] {
 *   background-color: #eee;
 *   opacity: 1;
 *   cursor: not-allowed;
 * }
 *
 * NO CHANGES to logic, behavior, or structure - only visual parity.
 */

export interface UiInputProps extends InputHTMLAttributes<HTMLInputElement> {
  hasError?: boolean;
}

export const UiInput = forwardRef<HTMLInputElement, UiInputProps>(
  ({ hasError, className = '', ...props }, ref) => {
    // Base classes matching .form-control from Bootstrap
    const baseClasses = [
      'block', // display: block
      'w-full', // width: 100%
      'h-angular-input', // height: 34px
      'px-3', // padding: 6px 12px (horizontal = 12px)
      'py-[6px]', // padding: 6px 12px (vertical = 6px)
      'text-angular-base', // font-size: 14px
      'leading-angular-base', // line-height: 1.42857143
      'text-[#555]', // color: #555
      'bg-white', // background-color: #fff
      'border', // border: 1px solid
      'border-solid',
      'rounded-angular', // border-radius: 4px
      'transition-all', // transition: border-color, box-shadow ease-in-out .15s
      'duration-150',
      'ease-in-out',
    ].join(' ');

    // Border color classes
    const borderClasses = hasError
      ? 'border-angular-error-highlight' // Error state: pure red (#ff0000) — matches AngularJS `border: 1px solid red`
      : 'border-angular-border-input focus:border-angular-focus'; // Normal: #ccc, Focus: #66afe9

    // Box shadow classes matching .form-control
    const shadowClasses = hasError
      ? 'focus:shadow-angular-input-error-focus'
      : 'shadow-angular-input focus:shadow-angular-input-focus';

    // Disabled/readonly state matching Bootstrap 3 .form-control[disabled]
    // BS3: background-color: #eee; opacity: 1; cursor: not-allowed;
    const disabledClasses =
      props.disabled || props.readOnly
        ? hasError
          ? '!bg-angular-bg-disabled !text-[#555] !cursor-not-allowed !opacity-100'
          : '!bg-angular-bg-disabled !text-[#555] !cursor-not-allowed !opacity-100 !border-angular-border-input'
        : '';

    // Focus outline removal (Bootstrap sets outline: 0 on focus)
    const focusClasses = 'focus:outline-none'; // outline: 0

    return (
      <input
        ref={ref}
        aria-invalid={hasError || undefined}
        className={`${baseClasses} ${borderClasses} ${shadowClasses} ${disabledClasses} ${focusClasses} ${className}`}
        {...props}
      />
    );
  }
);

UiInput.displayName = 'UiInput';
